import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
  Collapse,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Savings as SavingsIcon,
  School as EducationIcon,
  Home as HomeIcon,
  DirectionsCar as AutoIcon,
  FlightTakeoff as TravelIcon,
  LocalHospital as EmergencyIcon,
  MoreHoriz as OtherIcon,
  AddCircle as AddCircleIcon,
  AccountBalance as BankIcon,
  TrendingUp as InvestmentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  Schedule as ScheduleIcon,
  Note as NoteIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { createSavings, updateSavings, deleteSavings, recordSavingsContribution } from '../services/cashFlowService';
import savingsService from '../../../services/savingsService';
import SavingsStatsDashboard from '../../wallet/components/SavingsStatsDashboard';

const SAVINGS_TYPE_ICONS = {
  'Emergency Fund': EmergencyIcon,
  'Education': EducationIcon,
  'Home': HomeIcon,
  'Vehicle': AutoIcon,
  'Travel': TravelIcon,
  'Retirement': SavingsIcon,
  'Other': OtherIcon,
  'Savings': BankIcon,
  'Investment': InvestmentIcon
};

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
  { value: 'one-time', label: 'One-time' }
];

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

/**
 * Enhanced Savings Card Component with inline editing
 */
const EnhancedSavingsCard = ({ saving, onEdit, onDelete, onAddContribution, isOnlySaving = false }) => {
  const [expanded, setExpanded] = useState(isOnlySaving);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  const IconComponent = SAVINGS_TYPE_ICONS[saving.type] || OtherIcon;
  const progress = saving.targetAmount > 0 ? (saving.currentAmount / saving.targetAmount) * 100 : 0;

  // Initialize edit form data
  const initializeEditForm = () => {
    setEditFormData({
      name: saving.name || '',
      type: saving.type || '',
      priority: saving.priority || 'medium',
      targetAmount: saving.targetAmount || '',
      currentAmount: saving.currentAmount || '',
      targetDate: saving.targetDate ? saving.targetDate.split('T')[0] : '',
      frequency: saving.frequency || '',
      contributionAmount: saving.contributionAmount || '',
      state: saving.state || '',
      isActive: saving.isActive !== undefined ? saving.isActive : true,
      autoContribution: saving.autoContribution || false,
      notes: saving.notes || ''
    });
  };

  // Handle edit form field changes
  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate timeline when amounts or dates change
    if (field === 'targetAmount' || field === 'currentAmount' || field === 'contributionAmount' || field === 'frequency') {
      const target = parseFloat(field === 'targetAmount' ? value : editFormData.targetAmount) || 0;
      const current = parseFloat(field === 'currentAmount' ? value : editFormData.currentAmount) || 0;
      const contribution = parseFloat(field === 'contributionAmount' ? value : editFormData.contributionAmount) || 0;
      const frequency = field === 'frequency' ? value : editFormData.frequency;
      
      if (target > current && contribution > 0) {
        const remaining = target - current;
        let monthsToGoal = 0;
        
        switch (frequency) {
          case 'weekly':
            monthsToGoal = (remaining / contribution) / 4.33; // Average weeks per month
            break;
          case 'bi-weekly':
            monthsToGoal = (remaining / contribution) / 2.17; // Average bi-weeks per month
            break;
          case 'monthly':
            monthsToGoal = remaining / contribution;
            break;
          case 'quarterly':
            monthsToGoal = (remaining / contribution) * 3;
            break;
          case 'annually':
            monthsToGoal = (remaining / contribution) * 12;
            break;
        }
        
        if (monthsToGoal > 0) {
          const completionDate = new Date();
          completionDate.setMonth(completionDate.getMonth() + Math.ceil(monthsToGoal));
          setEditFormData(prev => ({
            ...prev,
            calculatedCompletionDate: completionDate.toISOString().split('T')[0]
          }));
        }
      }
    }
  };

  const handleEditClick = () => {
    initializeEditForm();
    setIsEditMode(true);
    setExpanded(true); // Auto-expand when editing
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
  };

  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare update data
      const updateData = {
        name: editFormData.name,
        type: editFormData.type,
        priority: editFormData.priority,
        targetAmount: parseFloat(editFormData.targetAmount) || 0,
        currentAmount: parseFloat(editFormData.currentAmount) || 0,
        targetDate: editFormData.targetDate || null,
        frequency: editFormData.frequency,
        contributionAmount: parseFloat(editFormData.contributionAmount) || 0,
        state: editFormData.state,
        isActive: editFormData.isActive,
        autoContribution: editFormData.autoContribution,
        notes: editFormData.notes
      };

      // Call the onEdit function with saving ID and update data
      if (typeof onEdit === 'function') {
        await onEdit(saving._id, updateData);
      }
      
      setIsEditMode(false);
      setEditFormData({});
    } catch (error) {
      console.error('Error saving savings goal:', error);
      // Handle error (show notification, etc.)
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'from-red-500 to-red-600';
      case 'medium': return 'from-yellow-500 to-yellow-600';
      case 'low': return 'from-green-500 to-green-600';
      default: return 'from-blue-500 to-blue-600';
    }
  };

  const getProgressColor = () => {
    if (progress >= 100) return '#10b981'; // green - completed
    if (progress >= 75) return '#3b82f6'; // blue - almost there
    if (progress >= 50) return '#f59e0b'; // amber - halfway
    if (progress >= 25) return '#f97316'; // orange - started
    return '#ef4444'; // red - just started
  };

  return (
    <Card className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 bg-gradient-to-br from-white via-white to-gray-50/50 backdrop-blur-sm mb-4">
      <CardContent className="p-0">
        {/* Header Section */}
        <div className={`bg-gradient-to-r ${getPriorityColor(saving.priority)} p-6 text-white`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              {isEditMode ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editFormData.name || ''}
                    onChange={(e) => handleEditFieldChange('name', e.target.value)}
                    className="w-full px-3 py-2 text-lg font-bold bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                    placeholder="Savings goal name"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={editFormData.type || ''}
                      onChange={(e) => handleEditFieldChange('type', e.target.value)}
                      className="px-3 py-1 text-sm bg-white/20 text-white border border-white/30 rounded"
                    >
                      <option value="">Select Type</option>
                      <option value="Emergency Fund">Emergency Fund</option>
                      <option value="Education">Education</option>
                      <option value="Home">Home</option>
                      <option value="Vehicle">Vehicle</option>
                      <option value="Travel">Travel</option>
                      <option value="Retirement">Retirement</option>
                      <option value="Other">Other</option>
                    </select>
                    <select
                      value={editFormData.priority || ''}
                      onChange={(e) => handleEditFieldChange('priority', e.target.value)}
                      className="px-3 py-1 text-sm bg-white/20 text-white border border-white/30 rounded"
                    >
                      <option value="high">High Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="low">Low Priority</option>
                    </select>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center mb-2">
                    <div className="p-2 bg-white/20 rounded-lg mr-3">
                      <IconComponent className="text-2xl text-white" />
                    </div>
                    <div>
                      <Typography variant="h5" className="font-bold text-white">
                        {saving.name}
                      </Typography>
                      <Typography variant="body2" className="text-white/80">
                        {saving.type} • {saving.priority} priority
                      </Typography>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-3">
                    {saving.isActive && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        className="bg-green-500/30 text-green-100 border-green-300/30" 
                      />
                    )}
                    {saving.autoContribution && (
                      <Chip 
                        label="Auto Contribution" 
                        size="small" 
                        className="bg-blue-500/30 text-blue-100 border-blue-300/30" 
                      />
                    )}
                    {progress >= 100 && (
                      <Chip 
                        label="Goal Achieved!" 
                        size="small" 
                        className="bg-yellow-500/30 text-yellow-100 border-yellow-300/30" 
                      />
                    )}
                  </div>
                </>
              )}
            </div>
            
            {/* Edit/Save/Cancel Actions or Amount Display */}
            <div className="text-right">
              {isEditMode ? (
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <button
                      onClick={handleEditSave}
                      disabled={isSaving}
                      className="px-3 py-1 bg-white/20 text-white border border-white/30 rounded hover:bg-white/30 transition-colors text-sm disabled:opacity-50"
                      title="Save changes"
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleEditCancel}
                      className="px-3 py-1 bg-white/20 text-white border border-white/30 rounded hover:bg-white/30 transition-colors text-sm"
                      title="Cancel editing"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="space-y-1">
                    <input
                      type="number"
                      value={editFormData.currentAmount || ''}
                      onChange={(e) => handleEditFieldChange('currentAmount', e.target.value)}
                      className="w-32 px-3 py-1 text-right text-sm bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                      placeholder="Current"
                      step="0.01"
                    />
                    <input
                      type="number"
                      value={editFormData.targetAmount || ''}
                      onChange={(e) => handleEditFieldChange('targetAmount', e.target.value)}
                      className="w-32 px-3 py-1 text-right text-sm bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                      placeholder="Target"
                      step="0.01"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleEditClick}
                    className="mb-2 px-3 py-1 bg-white/20 text-white border border-white/30 rounded hover:bg-white/30 transition-colors text-sm"
                    title="Edit savings goal"
                  >
                    Edit
                  </button>
                  <div className="text-3xl font-bold text-white">
                    {formatCurrency(saving.currentAmount)}
                  </div>
                  <div className="text-sm text-white/80">
                    of {formatCurrency(saving.targetAmount)}
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    {progress.toFixed(1)}% complete
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="p-6">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Savings Progress</span>
              <span className="text-sm text-gray-600">
                {formatCurrency(saving.targetAmount - saving.currentAmount)} remaining
              </span>
            </div>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(progress, 100)}
              className="h-3 rounded-full"
              sx={{
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getProgressColor(),
                  borderRadius: '9999px'
                }
              }}
            />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <InvestmentIcon className="text-blue-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-blue-700">
                {formatCurrency(saving.contributionAmount || 0)}
              </div>
              <div className="text-xs text-blue-600">
                {saving.frequency || 'Monthly'} Goal
              </div>
            </div>
            
            <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
              <CalendarTodayIcon className="text-green-600 mx-auto mb-2" />
              <div className="text-sm font-bold text-green-700">
                {formatDate(saving.targetDate)}
              </div>
              <div className="text-xs text-green-600">Target Date</div>
            </div>
            
            <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
              <CheckCircleIcon className="text-purple-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-purple-700">
                {progress >= 100 ? '✓' : Math.ceil((saving.targetAmount - saving.currentAmount) / (saving.contributionAmount || 1))}
              </div>
              <div className="text-xs text-purple-600">
                {progress >= 100 ? 'Complete' : 'Payments Left'}
              </div>
            </div>
          </div>

          {/* Expand Button and Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-2">
            <Button
              onClick={() => setExpanded(!expanded)}
              startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              className="text-blue-600 hover:bg-blue-50 w-full sm:w-auto"
              size="small"
            >
              {expanded ? 'Hide Details' : 'View Details'}
            </Button>
            
            {!isEditMode && (
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onAddContribution(saving)}
                  className="border-2 text-green-600 border-green-600 hover:bg-green-50 text-xs sm:text-sm px-2 sm:px-4 w-full sm:w-auto"
                >
                  Add Contribution
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onEdit(saving._id)}
                  className={`bg-gradient-to-r ${getPriorityColor(saving.priority)} text-white hover:opacity-90 text-xs sm:text-sm px-2 sm:px-4 w-full sm:w-auto`}
                >
                  Full Edit
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onDelete(saving._id)}
                  className="border-2 text-red-600 border-red-600 hover:bg-red-50 text-xs sm:text-sm px-2 sm:px-4 w-full sm:w-auto"
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Expandable Details Section */}
        <Collapse in={expanded}>
          <div className="px-6 pb-6">
            <Divider className="mb-4" />
            
            {isEditMode ? (
              <div className="space-y-4">
                {/* Goal Details Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Goal Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
                      <input
                        type="date"
                        value={editFormData.targetDate || ''}
                        onChange={(e) => handleEditFieldChange('targetDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contribution Frequency</label>
                      <select
                        value={editFormData.frequency || ''}
                        onChange={(e) => handleEditFieldChange('frequency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Frequency</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Planned Contribution</label>
                      <input
                        type="number"
                        value={editFormData.contributionAmount || ''}
                        onChange={(e) => handleEditFieldChange('contributionAmount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    {editFormData.calculatedCompletionDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Calculated Completion</label>
                        <input
                          type="date"
                          value={editFormData.calculatedCompletionDate}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Location & Settings */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Location & Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        value={editFormData.state || ''}
                        onChange={(e) => handleEditFieldChange('state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select State</option>
                        {US_STATES.map(state => (
                          <option key={state.value} value={state.value}>{state.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editFormData.isActive !== undefined ? editFormData.isActive : true}
                            onChange={(e) => handleEditFieldChange('isActive', e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Goal is Active</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editFormData.autoContribution || false}
                            onChange={(e) => handleEditFieldChange('autoContribution', e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Auto Contribution</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editFormData.notes || ''}
                    onChange={(e) => handleEditFieldChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notes about this savings goal..."
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              <>
                <Typography variant="h6" className="text-gray-800 mb-4 font-semibold">
                  Savings Goal Details
                </Typography>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Goal Information */}
                  <div className="p-4 rounded-xl bg-blue-50 border">
                    <div className="flex items-center mb-3">
                      <FlagIcon className="text-blue-600 mr-2" />
                      <Typography variant="subtitle1" className="font-semibold text-blue-700">
                        Goal Information
                      </Typography>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Priority:</span>
                        <span className="font-medium capitalize">{saving.priority}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Target Date:</span>
                        <span className="font-medium">{formatDate(saving.targetDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium ${progress >= 100 ? 'text-green-600' : 'text-blue-600'}`}>
                          {progress >= 100 ? 'Goal Achieved!' : 'In Progress'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contribution Schedule */}
                  <div className="p-4 rounded-xl bg-green-50 border">
                    <div className="flex items-center mb-3">
                      <ScheduleIcon className="text-green-600 mr-2" />
                      <Typography variant="subtitle1" className="font-semibold text-green-700">
                        Contribution Schedule
                      </Typography>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Frequency:</span>
                        <span className="font-medium capitalize">{saving.frequency || 'Not Set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-bold text-green-600">
                          {formatCurrency(saving.contributionAmount || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Auto Contribution:</span>
                        <span className="font-medium">
                          {saving.autoContribution ? 'Enabled' : 'Manual'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {saving.notes && (
                    <div className="p-4 rounded-xl bg-gray-50 border md:col-span-2">
                      <div className="flex items-center mb-3">
                        <NoteIcon className="text-gray-600 mr-2" />
                        <Typography variant="subtitle1" className="font-semibold text-gray-700">
                          Notes
                        </Typography>
                      </div>
                      <Typography variant="body2" className="text-gray-700 whitespace-pre-wrap">
                        {saving.notes}
                      </Typography>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const SavingsSection = ({ savings, onSavingsChange, isPartnerMode = false, partnerName = '' }) => {
  const [savingsAccounts, setSavingsAccounts] = useState([]);
  const [savingsGoals, setSavingsGoals] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [activeGoalsTab, setActiveGoalsTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openContributionDialog, setOpenContributionDialog] = useState(false);
  const [selectedSavings, setSelectedSavings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    targetAmount: '',
    currentAmount: '',
    targetDate: '',
    frequency: 'monthly',
    contributionAmount: '',
    priority: 'medium',
    notes: '',
    status: 'active'
  });
  const [contributionData, setContributionData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Use savings data from props when in partner mode or when available
  useEffect(() => {
    if (isPartnerMode && savings) {
      console.log('🤝 Partner mode: Using savings data from props:', savings);
      // In partner mode, use the data passed from parent
      setSavingsGoals(Array.isArray(savings) ? savings : []);
      setLoadingGoals(false);
      
      // Partner mode doesn't show accounts (only goals)
      setSavingsAccounts([]);
      setLoadingAccounts(false);
    } else if (!isPartnerMode) {
      // In user mode, fetch user's own data
      fetchSavingsAccounts();
      fetchSavingsGoals();
    }
  }, [savings, isPartnerMode]);

  const fetchSavingsAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const url = `${import.meta.env.VITE_API_URL}/api/wallet/savings-accounts`;
      console.log('🏦 Fetching savings accounts from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🏦 Savings accounts response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🏦 Savings accounts response data:', data);
        console.log('🏦 Savings accounts array:', data.data);
        console.log('🏦 Number of savings accounts:', data.data?.length || 0);
        setSavingsAccounts(data.data || []);
      } else {
        console.error('Failed to fetch savings accounts, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error fetching savings accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchSavingsGoals = async () => {
    try {
      setLoadingGoals(true);
      const goals = await savingsService.getAllSavings();
      console.log('🔍 All fetched goals:', goals);
      
      // Filter out wallet accounts, keep only manual goals
      const manualGoals = goals.filter(goal => !goal.isWalletAccount);
      console.log('🔍 Manual goals after filtering:', manualGoals);
      console.log('🔍 Filtered out goals:', goals.filter(goal => goal.isWalletAccount));
      
      setSavingsGoals(manualGoals);
    } catch (error) {
      console.error('Error fetching savings goals:', error);
    } finally {
      setLoadingGoals(false);
    }
  };

  const handleGoalsTabChange = (event, newValue) => {
    setActiveGoalsTab(newValue);
  };

  const handleOpenDialog = (saving = null) => {
    if (saving) {
      setFormData({
        name: saving.name || '',
        type: saving.type || '',
        targetAmount: saving.targetAmount?.toString() || '',
        currentAmount: saving.currentAmount?.toString() || '0',
        targetDate: saving.targetDate ? new Date(saving.targetDate).toISOString().split('T')[0] : '',
        frequency: saving.frequency || 'monthly',
        contributionAmount: saving.contributionAmount?.toString() || '0',
        priority: saving.priority || 'medium',
        notes: saving.notes || '',
        status: saving.status || 'active'
      });
      setSelectedSavings(saving);
    } else {
      setFormData({
        name: '',
        type: '',
        targetAmount: '',
        currentAmount: '0',
        targetDate: '',
        frequency: 'monthly',
        contributionAmount: '0',
        priority: 'medium',
        notes: '',
        status: 'active'
      });
      setSelectedSavings(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSavings(null);
    // Reset form data when closing dialog
    setFormData({
      name: '',
      type: '',
      targetAmount: '',
      currentAmount: '0',
      targetDate: '',
      frequency: 'monthly',
      contributionAmount: '0',
      priority: 'medium',
      notes: '',
      status: 'active'
    });
  };

  const handleOpenContributionDialog = (saving) => {
    setSelectedSavings(saving);
    setContributionData({
      amount: saving.contributionAmount || '',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setOpenContributionDialog(true);
  };

  const handleCloseContributionDialog = () => {
    setOpenContributionDialog(false);
    setSelectedSavings(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Separate handler for MUI Select components
  const handleSelectChange = (fieldName) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [fieldName]: value
    });
  };

  const handleContributionInputChange = (e) => {
    const { name, value } = e.target;
    setContributionData({
      ...contributionData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const savingsData = {
        ...formData,
        targetAmount: parseFloat(formData.targetAmount) || 0,
        currentAmount: parseFloat(formData.currentAmount) || 0,
        contributionAmount: parseFloat(formData.contributionAmount) || 0,
        frequency: formData.frequency || 'monthly',
        priority: formData.priority || 'medium',
        status: formData.status || 'active',
        targetDate: formData.targetDate || null
      };

      console.log('💾 Saving savings goal:', savingsData);

      let updatedSavings;
      if (selectedSavings) {
        console.log('✏️ Updating existing savings goal:', selectedSavings._id);
        updatedSavings = await savingsService.updateSavings(selectedSavings._id, savingsData);
        console.log('✅ Updated savings goal:', updatedSavings);
        setSavingsGoals(savingsGoals.map(s => s._id === selectedSavings._id ? updatedSavings : s));
      } else {
        console.log('➕ Creating new savings goal');
        updatedSavings = await savingsService.createSavings(savingsData);
        console.log('✅ Created savings goal:', updatedSavings);
        setSavingsGoals(prevGoals => {
          const newGoals = [...prevGoals, updatedSavings];
          console.log('📋 Updated savings goals list:', newGoals);
          return newGoals;
        });
      }

      handleCloseDialog();
      
      // Refresh savings goals list to ensure UI is up to date
      if (!isPartnerMode) {
        await fetchSavingsGoals();
      }
      
      // Trigger callback to parent component to refresh data
      if (onSavingsChange) {
        onSavingsChange();
      }
    } catch (error) {
      console.error('❌ Error saving savings goal:', error);
      // Show user-friendly error message
      alert('Failed to save savings goal. Please try again.');
    }
  };

  const handleContributionSubmit = async (e) => {
    e.preventDefault();
    try {
      const contribution = {
        ...contributionData,
        amount: parseFloat(contributionData.amount)
      };

      const updatedSavings = await savingsService.recordContribution(selectedSavings._id, contribution);
      setSavingsGoals(savingsGoals.map(s => s._id === selectedSavings._id ? updatedSavings : s));
      handleCloseContributionDialog();
      
      // Refresh savings goals list to ensure UI is up to date
      if (!isPartnerMode) {
        await fetchSavingsGoals();
      }
    } catch (error) {
      console.error('Error recording contribution:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this savings goal?')) {
      try {
        await savingsService.deleteSavings(id);
        setSavingsGoals(savingsGoals.filter(s => s._id !== id));
        
        // Refresh savings goals list to ensure UI is up to date
        if (!isPartnerMode) {
          await fetchSavingsGoals();
        }
      } catch (error) {
        console.error('Error deleting savings goal:', error);
      }
    }
  };

  const filteredGoals = savingsGoals.filter(saving => {
    if (activeGoalsTab === 0) return saving.status === 'active';
    if (activeGoalsTab === 1) return saving.status === 'completed';
    return true;
  });

  const calculateProgress = (saving) => {
    return (saving.currentAmount / saving.targetAmount) * 100;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Add SavingsStatsDashboard at the top */}
      <SavingsStatsDashboard />
      
      {/* Active Savings Section */}
      <Box sx={{ width: '100%', mb: 6 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 3,
          gap: 2
        }}>
          <Typography variant="h5" sx={{ color: '#16a34a', fontWeight: 600 }}>
            💰 {isPartnerMode ? `${partnerName}'s Savings` : 'Active Savings'}
          </Typography>
          <Chip 
            label={`${savingsAccounts.length} Account${savingsAccounts.length !== 1 ? 's' : ''}`}
            color="success"
            variant="outlined"
          />
        </Box>

        {loadingAccounts ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading savings accounts...</Typography>
          </Paper>
        ) : savingsAccounts.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No savings accounts found in your wallet. Add savings or investment accounts to see them here.
            </Typography>
          </Paper>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {savingsAccounts.map((account) => {
              const IconComponent = SAVINGS_TYPE_ICONS[account.accountType] || BankIcon;
              const accountName = account.bankName || account.name || 'Savings Account';
              const accountType = account.accountType || 'Savings';
              const lastFour = account.lastFourDigits || '';
              const balance = account.balance || 0;
              const institutionName = account.institutionName || 'Manual Entry';
              
              return (
                <div
                  key={account._id}
                  className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] sm:shadow-[0_15px_45px_-12px_rgba(0,0,0,0.5)] hover:shadow-[0_20px_55px_-12px_rgba(0,0,0,0.6)] rounded-xl p-3 sm:p-4 md:p-5 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-2px] flex flex-col items-center"
                >
                  {/* Icon - On its own line at top, centered */}
                  <div className="bg-gradient-to-br from-emerald-100 to-green-100 p-2 sm:p-2.5 md:p-3 rounded-lg sm:rounded-xl shadow-md flex-shrink-0 mb-2 sm:mb-3 flex items-center justify-center">
                    <div className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 flex items-center justify-center text-emerald-600">
                      <IconComponent className="h-full w-full" />
                    </div>
                  </div>
                  
                  {/* Account Name - Centered */}
                  <Typography 
                    variant="h6" 
                    className="text-sm sm:text-base md:text-lg font-bold text-gray-800 text-center mb-1 sm:mb-1.5 px-2 line-clamp-2"
                  >
                    {accountName}
                  </Typography>
                  
                  {/* Account Type and Number - Centered */}
                  <Typography 
                    variant="body2" 
                    className="text-[10px] sm:text-xs md:text-sm text-gray-600 text-center mb-2 sm:mb-3 px-2"
                  >
                    {accountType}{lastFour ? ` • ****${lastFour}` : ''}
                  </Typography>
                  
                  {/* Balance - On its own line, centered, in green */}
                  <Typography 
                    variant="h5" 
                    className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-600 text-center mb-1 sm:mb-1.5 px-2"
                  >
                    {formatCurrency(balance)}
                  </Typography>
                  
                  {/* Institution Name - Centered */}
                  <Typography 
                    variant="body2" 
                    className="text-[10px] sm:text-xs md:text-sm text-gray-500 text-center px-2"
                  >
                    {institutionName}
                  </Typography>
                </div>
              );
            })}
          </div>
        )}
      </Box>

      {/* Goals Section */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 3,
          gap: 2
        }}>
          <Typography variant="h5" sx={{ color: '#dc2626', fontWeight: 600 }}>
            🎯 Goals
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Add Savings Goal
          </Button>
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeGoalsTab} 
            onChange={handleGoalsTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: 'auto', sm: 160 },
                padding: { xs: '6px 8px', sm: '12px 16px' }
              }
            }}
          >
            <Tab label="Active" />
            <Tab label="Completed" />
            <Tab label="All" />
          </Tabs>
        </Paper>

        {loadingGoals ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading savings goals...</Typography>
          </Paper>
        ) : filteredGoals.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No {activeGoalsTab === 0 ? 'active' : activeGoalsTab === 1 ? 'completed' : ''} savings goals found.
            </Typography>
          </Paper>
        ) : (
          <div className="space-y-4">
            {filteredGoals.map((saving) => (
              <EnhancedSavingsCard
                key={saving._id}
                saving={saving}
                onEdit={handleOpenDialog}
                onDelete={handleDelete}
                onAddContribution={handleOpenContributionDialog}
                isOnlySaving={filteredGoals.length === 1}
              />
            ))}
          </div>
        )}
      </Box>

      {/* Add/Edit Savings Goal Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSavings ? 'Edit Savings Goal' : 'Add Savings Goal'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Goal Name"
                  fullWidth
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleSelectChange('type')}
                    label="Type"
                  >
                    <MenuItem value="Emergency Fund">Emergency Fund</MenuItem>
                    <MenuItem value="Education">Education</MenuItem>
                    <MenuItem value="Home">Home</MenuItem>
                    <MenuItem value="Vehicle">Vehicle</MenuItem>
                    <MenuItem value="Travel">Travel</MenuItem>
                    <MenuItem value="Retirement">Retirement</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleSelectChange('priority')}
                    label="Priority"
                  >
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="targetAmount"
                  label="Target Amount"
                  type="number"
                  fullWidth
                  value={formData.targetAmount}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="currentAmount"
                  label="Current Amount"
                  type="number"
                  fullWidth
                  value={formData.currentAmount}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="targetDate"
                  label="Target Date"
                  type="date"
                  fullWidth
                  value={formData.targetDate}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Contribution Frequency</InputLabel>
                  <Select
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleSelectChange('frequency')}
                    label="Contribution Frequency"
                  >
                    {FREQUENCIES.map((freq) => (
                      <MenuItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="contributionAmount"
                  label="Planned Contribution Amount"
                  type="number"
                  fullWidth
                  value={formData.contributionAmount}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes"
                  multiline
                  rows={2}
                  fullWidth
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedSavings ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add Contribution Dialog */}
      <Dialog open={openContributionDialog} onClose={handleCloseContributionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Contribution</DialogTitle>
        <form onSubmit={handleContributionSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="amount"
                  label="Contribution Amount"
                  type="number"
                  fullWidth
                  value={contributionData.amount}
                  onChange={handleContributionInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="date"
                  label="Date"
                  type="date"
                  fullWidth
                  value={contributionData.date}
                  onChange={handleContributionInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes"
                  multiline
                  rows={2}
                  fullWidth
                  value={contributionData.notes}
                  onChange={handleContributionInputChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseContributionDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Add Contribution
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default SavingsSection; 